/**
 * Copia um texto para a área de transferência, com plano B.
 *
 * ## Os dois modos de falha, e por que o plano B não é só para navegador velho
 *
 * 1. `navigator.clipboard` **não existe** fora de https nem em navegador
 *    antigo -- e o AloDesk roda em http na rede interna.
 * 2. Ele existe e **recusa**: a aba precisa estar em foco e com permissão.
 *    Medido na tela em 02/11/2026 -- o botão "Copiar link" disse "não foi
 *    possível copiar" num navegador moderno, em localhost, com `writeText`
 *    presente.
 *
 * A primeira versão deste módulo só cobria o caso 1: escolhia o caminho pela
 * EXISTÊNCIA da API e, se ela estourasse, desistia sem tentar o resto. Agora o
 * plano B é tentado nos dois casos -- é por isso que ele não está dentro de um
 * `else`.
 *
 * ## Por que um módulo
 *
 * Esta mesma rotina está copiada e colada em cinco telas (central de ajuda,
 * vídeos, WhatsApp, template, criação de tenant), e **todas** têm o defeito
 * acima. Elas não foram migradas neste trabalho -- cada uma tem o seu próprio
 * aviso --, mas quem for mexer numa delas tem aqui para onde apontar, em vez
 * de criar a sexta cópia.
 */

/**
 * O `textarea` escondido + `execCommand`, que funciona sem permissão nenhuma.
 *
 * A caixa é removida sempre: deixada na página, cada cópia somaria um campo
 * invisível ao fim do documento -- e campo invisível é alcançável pelo Tab e
 * pelo leitor de tela.
 */
const copiarPelaCaixaEscondida = (valor) => {
    let caixa = null;

    try {
        caixa = document.createElement('textarea');
        caixa.value = valor;
        caixa.setAttribute('readonly', '');
        caixa.style.position = 'fixed';
        caixa.style.opacity = '0';
        document.body.appendChild(caixa);
        caixa.select();

        return document.execCommand('copy') !== false;
    } catch (e) {
        return false;
    } finally {
        if (caixa && caixa.parentNode) caixa.parentNode.removeChild(caixa);
    }
};

/**
 * @param {string} texto
 * @returns {Promise<boolean>} `true` se copiou
 */
export const copiarTexto = async (texto) => {
    const valor = texto === null || texto === undefined ? '' : String(texto);

    if (!valor) return false;

    if (navigator?.clipboard?.writeText) {
        try {
            await navigator.clipboard.writeText(valor);

            return true;
        } catch (e) {
            // Sem `return`: a recusa da API moderna cai no plano B logo
            // abaixo. Era aqui que a primeira versão desistia.
        }
    }

    return copiarPelaCaixaEscondida(valor);
};

export default copiarTexto;
